import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { map } from 'rxjs';
import { AuthService } from '../../../auth/auth.service';
import { NotificationsService } from '../../../services/notifications.service';

interface IRoute {
  path: string;
  name: string;
  icon: string;
}

interface DashboardHeaderNavViewModel {
  routes: IRoute[];
  notificationsPath: string;
}

function buildRoutes({
  hasAdminProjectsAccess,
  hasFullAdminOrgAccess,
  canAccessBatteryCatalogAdmin,
  canAdvanceOrderStatus,
  canAccessResellerWorkspace,
  isInAdminOrg,
  isSuperAdmin,
}: {
  hasAdminProjectsAccess: boolean;
  hasFullAdminOrgAccess: boolean;
  canAccessBatteryCatalogAdmin: boolean;
  canAdvanceOrderStatus: boolean;
  canAccessResellerWorkspace: boolean;
  isInAdminOrg: boolean;
  isSuperAdmin: boolean;
}): IRoute[] {
  const isSupportAdminActor = hasAdminProjectsAccess && !hasFullAdminOrgAccess;

  if (isSupportAdminActor) {
    return [
      {
        path: '/admin/projects',
        name: 'Projects',
        icon: 'assets/icons/navigation.svg#projects',
      },
      {
        path: '/admin/work-orders',
        name: 'Work Orders',
        icon: 'assets/icons/navigation.svg#stack',
      },
      ...(canAdvanceOrderStatus
        ? [
            {
              path: '/admin/production',
              name: 'Production',
              icon: 'assets/icons/navigation.svg#stack',
            },
          ]
        : []),
      ...(canAccessBatteryCatalogAdmin
        ? [
            {
              path: '/admin/batteries',
              name: 'Batteries',
              icon: 'assets/icons/navigation.svg#battery',
            },
          ]
        : []),
    ];
  }

  return [
    ...(hasFullAdminOrgAccess
      ? [
          {
            path: '/admin/dashboard',
            name: 'Admin',
            icon: 'assets/icons/navigation.svg#admin',
          },
          {
            path: '/admin/resellers',
            name: 'Resellers',
            icon: 'assets/icons/navigation.svg#customers',
          },
        ]
      : []),
    ...(canAccessResellerWorkspace
      ? [
          {
            path: hasAdminProjectsAccess ? '/admin/customer-profiles' : '/customer-profiles',
            name: 'Customer Profiles',
            icon: 'assets/icons/navigation.svg#customers',
          },
        ]
      : []),
    {
      path: hasFullAdminOrgAccess ? '/admin/users' : '/users',
      name: 'Users',
      icon: 'assets/icons/navigation.svg#users',
    },
    {
      path: hasFullAdminOrgAccess ? '/admin/projects' : '/projects',
      name: 'Projects',
      icon: 'assets/icons/navigation.svg#projects',
    },
    {
      path: hasAdminProjectsAccess ? '/admin/work-orders' : '/work-orders',
      name: 'Work Orders',
      icon: 'assets/icons/navigation.svg#stack',
    },
    ...(canAdvanceOrderStatus
      ? [
          {
            path: '/admin/production',
            name: 'Production',
            icon: 'assets/icons/navigation.svg#stack',
          },
        ]
      : []),
    ...(isSuperAdmin
      ? [
          {
            path: '/admin/manufacturers',
            name: 'Manufacturers',
            icon: 'assets/icons/navigation.svg#stack',
          },
        ]
      : []),
    ...(canAccessResellerWorkspace && isInAdminOrg
      ? [
          {
            path: hasAdminProjectsAccess && canAccessBatteryCatalogAdmin ? '/admin/batteries' : '/batteries',
            name: 'Batteries',
            icon: 'assets/icons/navigation.svg#battery',
          },
        ]
      : []),
    ...(hasFullAdminOrgAccess
      ? [
          {
            path: '/admin/cables',
            name: 'Cables',
            icon: 'assets/icons/navigation.svg#stack',
          },
        ]
      : []),
    ...(hasFullAdminOrgAccess
      ? [
          {
            path: '/admin/installation-pricing',
            name: 'Installation Pricing',
            icon: 'assets/icons/navigation.svg#stack',
          },
        ]
      : []),
  ];
}

@Component({
    selector: 'dashboard-header-nav',
    templateUrl: './dashboard-header-nav.component.html',
    styleUrls: ['./dashboard-header-nav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DashboardHeaderNavComponent {
  private readonly authService = inject(AuthService);
  private readonly notificationsService = inject(NotificationsService);

  public readonly activeRequestCount$ = this.notificationsService.activeRequestCount$;
  public readonly viewModel$ = this.authService.user$.pipe(
    map((user): DashboardHeaderNavViewModel => {
      if (user == null) {
        return {
          routes: [],
          notificationsPath: '/notifications',
        };
      }

      const hasAdminProjectsAccess = this.authService.hasAdminProjectsAccessSync;
      const hasFullAdminOrgAccess = this.authService.hasFullAdminOrgAccessSync;

      return {
        routes: buildRoutes({
          hasAdminProjectsAccess,
          hasFullAdminOrgAccess,
          canAccessBatteryCatalogAdmin: this.authService.canAccessBatteryCatalogAdminSync,
          canAdvanceOrderStatus: this.authService.canAdvanceOrderStatusSync,
          canAccessResellerWorkspace: this.authService.canAccessResellerWorkspaceSync,
          isInAdminOrg: this.authService.isInAdminOrgSync,
          isSuperAdmin: this.authService.isSuperAdminSync,
        }),
        notificationsPath: hasAdminProjectsAccess ? '/admin/notifications' : '/notifications',
      };
    }),
  );
}
